import React from "react";
import { motion } from "framer-motion";
import {
  HiOutlineDocumentText,
  HiOutlineCalendar,
  HiOutlineUserCircle,
  HiOutlineShare,
} from "react-icons/hi2";
import { useLanguage } from "../../../contexts/LanguageContext";

const SERVICES = [
  {
    icon: HiOutlineDocumentText,
    labelKey: "serviceRecipesOnline",
    descKey: "serviceRecipesDesc",
    color: "from-orange-500 to-amber-500",
  },
  {
    icon: HiOutlineCalendar,
    labelKey: "serviceMealIdeas",
    descKey: "serviceMealDesc",
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: HiOutlineUserCircle,
    labelKey: "serviceMemberAccount",
    descKey: "serviceMemberDesc",
    color: "from-orange-400 to-amber-400",
  },
  {
    icon: HiOutlineShare,
    labelKey: "serviceShareRecipes",
    descKey: "serviceShareDesc",
    color: "from-amber-400 to-orange-400",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Section Nos services – design amélioré avec icônes, dégradés et animations
 */
const ServicesSection = () => {
  const { t } = useLanguage();
  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24 w-full min-w-0"
      initial={false}
      animate="visible"
      variants={containerVariants}
    >
      {/* Titre de section */}
      <motion.div
        className="text-center mb-12 sm:mb-14 md:mb-16"
        variants={titleVariants}
      >
        <motion.p
          className="text-white font-semibold text-sm uppercase tracking-widest mb-2"
          variants={titleVariants}
        >
          {t("servicesSubtitle")}
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3"
          variants={titleVariants}
        >
          {t("ourServices")}
        </motion.h2>
        <motion.div
          className="w-16 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mx-auto mb-3"
          variants={titleVariants}
        />
        <motion.p
          className="text-white text-sm sm:text-base max-w-xl mx-auto"
          variants={titleVariants}
        >
          {t("servicesIntro")}
        </motion.p>
      </motion.div>

      {/* Grille de services */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.article
              key={i}
              variants={itemVariants}
              className="group relative bg-black/35 sm:bg-white/10 backdrop-blur-none sm:backdrop-blur-md rounded-2xl p-6 sm:p-7 lg:p-8 shadow-lg border border-white/20 hover:border-orange-400/50 transition-all duration-300 overflow-hidden"
              whileHover={{
                scale: 1.03,
                y: -4,
                boxShadow: "0 20px 25px -5px rgba(249, 115, 22, 0.2)",
              }}
            >
              {/* Effet de fond au hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-amber-500/0 group-hover:from-orange-500/10 group-hover:to-amber-500/10 transition-all duration-300 rounded-2xl" />

              <div className="relative z-10">
                {/* Icône avec fond dégradé */}
                <motion.div
                  className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${s.color} text-white shadow-lg mb-5`}
                  whileHover={{ rotate: [0, -5, 5, 0], scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                >
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8" />
                </motion.div>

                {/* Titre */}
                <h3 className="font-bold text-white text-lg sm:text-xl mb-3">
                  {t(s.labelKey)}
                </h3>

                {/* Description */}
                <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                  {t(s.descKey)}
                </p>
              </div>
            </motion.article>
          );
        })}
      </div>
    </motion.section>
  );
};

export default ServicesSection;
